<template>
  <section class="container">
    <template v-if="hasTag && !hasTitle">
      <h2 class="tag">{{ data.tag }}</h2>
    </template>
    <template v-else-if="hasTitle">
      <div v-if="hasTag" class="tag">{{ data.tag }}</div>
      <h2 class="mb-12">{{ data.title }}</h2>
    </template>
    <ul
      class="grid list-none grid-cols-2 gap-x-4 gap-y-6 p-0 xs:grid-cols-3 lg:gap-x-0"
      :class="{
        'lg:grid-cols-5': data.links.length === 5,
        'lg:grid-cols-6': data.links.length === 6,
        'lg:grid-cols-7': data.links.length === 7,
        'lg:grid-cols-8': data.links.length === 8,
        'lg:grid-cols-9': data.links.length === 9,
      }"
    >
      <li
        v-for="link in data.links"
        :key="link.title"
        class="lg:border-r lg:border-grey-300 last:lg:border-none"
      >
        <NuxtLink
          :to="link.url"
          :external="link.external"
          class="group flex h-full w-full flex-col items-center justify-start gap-3 py-2 no-underline lg:px-4"
        >
          <component
            :is="link.icon"
            class="h-8 w-8 fill-green-800 duration-150 motion-safe:transition-colors"
          />
          <span
            class="text-center text-sm font-semibold uppercase duration-150 group-hover:text-green-800 motion-safe:transition-colors"
          >
            {{ link.title }}
          </span>
        </NuxtLink>
      </li>
    </ul>
  </section>
</template>
<script setup lang="ts">
type Links = "residential" | "customer" | "business";

const props = defineProps<{
  hasTag: boolean;
  alternativeTag?: string;
  hasTitle: boolean;
  links: Links;
}>();

interface LinksData {
  title: string;
  icon: Component | string;
  url: string;
  external: boolean;
}

const linksData: {
  [key in Links]: {
    tag: string;
    title: string;
    links: LinksData[];
  };
} = {
  residential: {
    tag: props.alternativeTag ?? "Quick links",
    title: "We've got something to suit every need",
    links: [
      {
        title: "Internet plans",
        icon: resolveComponent("IconNbn"),
        url: "https://www.aussiebroadband.com.au/internet/nbn-plans/",
        external: true,
      },
      {
        title: "Mobile SIM plans",
        icon: resolveComponent("IconMobile"),
        url: "/mobile/sim-only-plans/",
        external: true,
      },
      {
        title: "Mobile Broadband",
        icon: resolveComponent("IconWifi"),
        url: "/mobile/data-only-sim/",
        external: true,
      },
      {
        title: "Modems & Hardware",
        icon: resolveComponent("IconModem"),
        url: "https://www.aussiebroadband.com.au/internet/hardware/",
        external: true,
      },
      {
        title: "Home phone",
        icon: resolveComponent("IconPhone"),
        url: "/internet/home-phone/",
        external: true,
      },
    ],
  },
  customer: {
    tag: props.alternativeTag ?? "Quick links",
    title: "Get the help you need at hand",
    links: [
      {
        title: "MyAussie®",
        icon: resolveComponent("IconMyAussieApp"),
        url: "https://my.aussiebroadband.com.au/",
        external: true,
      },
      {
        title: "Activate SIM card",
        icon: resolveComponent("IconMobile"),
        url: "https://www.aussiebroadband.com.au/tools/activate-sim/",
        external: true,
      },
      {
        title: "Help Centre",
        icon: resolveComponent("IconHelpCentre"),
        url: "/help-centre/",
        external: false,
      },
      {
        title: "Refer a Friend",
        icon: resolveComponent("IconReferral"),
        url: "/refer-a-friend/",
        external: false,
      },
      {
        title: "Webmail",
        icon: resolveComponent("IconMail"),
        url: "https://webmail.aussiebroadband.com.au/",
        external: true,
      },
      {
        title: "Moving home",
        icon: resolveComponent("IconTruck"),
        url: "/moving-home/",
        external: true,
      },
      {
        title: "Speed test",
        icon: resolveComponent("IconSpeedTest"),
        url: "https://speed.aussiebroadband.com.au/",
        external: true,
      },
      {
        title: "Working from home",
        icon: resolveComponent("IconHouse"),
        url: "/help-centre/internet/working-from-home-faq/",
        external: false,
      },
      {
        title: "Upgrade my plan",
        icon: resolveComponent("IconUpgrade"),
        url: "#upgradePlan",
        external: false,
      },
    ],
  },
  business: {
    tag: props.alternativeTag ?? "Quick links",
    title: "Popular business telco solutions",
    links: [
      {
        title: "nbn® plans",
        icon: resolveComponent("IconNbn"),
        url: "https://www.aussiebroadband.com.au/business/internet/nbn-plans/",
        external: true,
      },
      {
        title: "Enterprise Ethernet",
        icon: resolveComponent("IconEnterpriseEthernet"),
        url: "https://www.aussiebroadband.com.au/business/internet/enterprise-ethernet/",
        external: true,
      },
      {
        title: "Mobile SIM plans",
        icon: resolveComponent("IconMobile"),
        url: "/business/mobile/sim-plans/",
        external: true,
      },
      {
        title: "Mobile Broadband",
        icon: resolveComponent("IconWifi"),
        url: "/business/mobile/mobile-broadband/",
        external: true,
      },
      {
        title: "VoIP plans",
        icon: resolveComponent("IconPhone"),
        url: "https://www.aussiebroadband.com.au/business/voice/voip-phone/",
        external: true,
      },
      {
        title: "Phone systems",
        icon: resolveComponent("IconHostedPhones"),
        url: "https://www.aussiebroadband.com.au/business/voice/hosted-pbx/",
        external: true,
      },
      {
        title: "Business hardware",
        icon: resolveComponent("IconModem"),
        url: "https://www.aussiebroadband.com.au/business/internet/network-devices/",
        external: true,
      },
    ],
  },
};

const data = linksData[props.links];
</script>
